/**
 * Shared App Proxy events-v fetch + in-flight/response cache.
 * Same product near US-center inventory queries coalesce to one network GET.
 * PDP still passes productGid when it needs a single-product slice.
 */
(function (w) {
  if (w.AMCWorkshopEventsV) return;

  var US_LAT = 39.833333;
  var US_LNG = -98.583333;
  var US_RADIUS = 5000;
  var inFlight = Object.create(null);
  var resolved = Object.create(null);
  var pageBootPromise = null;

  function roundCoord(n) {
    return Math.round(Number(n) * 1e4) / 1e4;
  }

  function isUsCenterInventory(lat, lng, radiusKm) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (Math.abs(lat - US_LAT) > 0.15 || Math.abs(lng - US_LNG) > 0.15) return false;
    if (!Number.isFinite(radiusKm) || radiusKm < 2000) return false;
    return true;
  }

  function eventsPathOf(opts) {
    var path = String((opts && opts.eventsPath) || '/events-v').trim();
    if (path.charAt(0) !== '/') path = '/' + path;
    return path;
  }

  function isDedicatedEventsPath(opts) {
    var path = eventsPathOf(opts);
    return path.indexOf('calendar') !== -1 || path.indexOf('venues-v') !== -1;
  }

  function cacheKey(opts) {
    var gid = String(opts.productGid || '').trim();
    var lat = Number(opts.lat);
    var lng = Number(opts.lng);
    var radiusKm = Number(opts.radiusKm);
    var empty = opts.includeEmpty === true;
    var path = eventsPathOf(opts);
    if (isDedicatedEventsPath(opts)) {
      return [
        'path',
        path,
        gid,
        opts.startDate || '',
        opts.endDate || '',
        roundCoord(lat),
        roundCoord(lng),
        Number.isFinite(radiusKm) ? radiusKm : '',
        empty ? '1' : '0'
      ].join('|');
    }
    /* US-center inventory: date-less callers coalesce to one full GET per
       product; date-windowed callers get their own slim key so they neither
       reuse nor overwrite the full inventory entry. */
    if (isUsCenterInventory(lat, lng, radiusKm)) {
      var invStart = opts.startDate || '';
      var invEnd = opts.endDate || '';
      if (invStart && invEnd) {
        return 'inv|' + gid + '|' + invStart + '|' + invEnd;
      }
      return 'inv|' + gid + '|0';
    }
    return [
      'raw',
      gid,
      roundCoord(lat),
      roundCoord(lng),
      Number.isFinite(radiusKm) ? radiusKm : '',
      empty ? '1' : '0',
      opts.startDate || '',
      opts.endDate || ''
    ].join('|');
  }

  function buildUrl(opts) {
    var proxy = String(opts.proxyBase || '/apps/workshop-events').replace(/\/+$/, '');
    var path = eventsPathOf(opts);
    var lat = Number(opts.lat);
    var lng = Number(opts.lng);
    var radiusKm = Number(opts.radiusKm);
    var empty = opts.includeEmpty === true;
    var startDate = opts.startDate || '';
    var endDate = opts.endDate || '';
    if (!isDedicatedEventsPath(opts) && isUsCenterInventory(lat, lng, radiusKm)) {
      lat = US_LAT;
      lng = US_LNG;
      radiusKm = US_RADIUS;
      empty = false;
      /* Keep startDate/endDate: date-windowed callers still hit US-center
         coordinates but ask the backend to trim events to their window. */
    }
    var gid = String(opts.productGid || '').trim();
    var url =
      proxy +
      path +
      '?lat=' +
      encodeURIComponent(lat) +
      '&lng=' +
      encodeURIComponent(lng) +
      '&radius_km=' +
      encodeURIComponent(radiusKm) +
      '&include_empty_venues=' +
      (empty ? 'true' : 'false');
    if (gid) url += '&product_gid=' + encodeURIComponent(gid);
    /* Backend requires both bounds together (date_from + date_to, YYYY-MM-DD);
       a lone bound is a 400, so only send a complete window. */
    if (startDate && endDate) {
      url += '&date_from=' + encodeURIComponent(startDate);
      url += '&date_to=' + encodeURIComponent(endDate);
    }
    return url;
  }

  function get(opts) {
    opts = opts || {};
    var gid = normalizeGid(opts.productGid);
    if (!gid) return Promise.reject(new Error('productGid required'));
    opts.productGid = gid;

    var key = cacheKey(opts);
    if (resolved[key]) {
      return Promise.resolve(resolved[key]);
    }
    if (inFlight[key]) return inFlight[key];

    var url = buildUrl(opts);
    var p = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('events-v ' + res.status);
        return res.json();
      })
      .then(function (data) {
        resolved[key] = data;
        delete inFlight[key];
        return data;
      })
      .catch(function (err) {
        delete inFlight[key];
        throw err;
      });

    inFlight[key] = p;
    return p;
  }

  function normalizeGid(raw) {
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return '';
    if (s.indexOf('gid://') === 0) return s;
    if (/^\d+$/.test(s)) return 'gid://shopify/Product/' + s;
    return s;
  }

  function uniqueGids(list) {
    var out = [];
    var seen = Object.create(null);
    (list || []).forEach(function (raw) {
      var gid = normalizeGid(raw);
      if (!gid || seen[gid]) return;
      seen[gid] = true;
      out.push(gid);
    });
    return out;
  }

  function parseJsonScript(el) {
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function collectPageGids() {
    var found = [];
    var nodes = document.querySelectorAll(
      '[data-wec-config], [data-wef-config], #venue-explorer-workshop-handles'
    );
    for (var i = 0; i < nodes.length; i++) {
      var data = parseJsonScript(nodes[i]);
      if (!data) continue;
      if (Array.isArray(data.gids)) found = found.concat(data.gids);
      if (data.catalog && typeof data.catalog === 'object') {
        Object.keys(data.catalog).forEach(function (type) {
          var entry = data.catalog[type];
          if (entry && entry.gid) found.push(entry.gid);
        });
      }
      ['mosaic', 'ceramic', 'flower', 'resin'].forEach(function (type) {
        var entry = data[type];
        if (entry && typeof entry === 'object' && entry.id) found.push(entry.id);
      });
    }
    return uniqueGids(found);
  }

  function prefetch(gids, opts) {
    opts = opts || {};
    return Promise.all(
      uniqueGids(gids).map(function (gid) {
        return get({
          productGid: gid,
          lat: opts.lat != null ? opts.lat : US_LAT,
          lng: opts.lng != null ? opts.lng : US_LNG,
          radiusKm: opts.radiusKm != null ? opts.radiusKm : US_RADIUS,
          includeEmpty: false,
          proxyBase: opts.proxyBase,
          eventsPath: opts.eventsPath
        }).catch(function () {
          return null;
        });
      })
    );
  }

  function bootPage(opts) {
    if (pageBootPromise) return pageBootPromise;
    pageBootPromise = Promise.resolve([]);
    return pageBootPromise;
  }

  function whenNearOrIdle(el, start, opts) {
    opts = opts || {};
    var started = false;
    function run() {
      if (started) return;
      started = true;
      if (typeof start === 'function') start();
    }
    whenNearViewport(el, run, { leadPx: opts.leadPx != null ? opts.leadPx : 1400 });
    var idleMs = opts.idleMs != null ? opts.idleMs : 900;
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(function () {
        run();
      }, { timeout: idleMs });
    } else {
      setTimeout(run, idleMs);
    }
    return run;
  }

  function schedulePageBoot() {
    /* Calendar and This Month’s fetch their own paths after idle. */
  }

  function isNearViewport(el, leadPx) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return true;
    var rect = el.getBoundingClientRect();
    var vh = window.innerHeight || 0;
    var lead = Number(leadPx);
    if (!Number.isFinite(lead) || lead < 0) lead = 280;
    return rect.top < vh + lead && rect.bottom > -64;
  }

  function whenNearViewport(el, start, opts) {
    opts = opts || {};
    var leadPx = opts.leadPx != null ? opts.leadPx : 280;
    var started = false;
    var io = null;

    function run() {
      if (started) return;
      started = true;
      if (io) {
        io.disconnect();
        io = null;
      }
      if (el) {
        el.removeEventListener('pointerdown', run);
        el.removeEventListener('focusin', run);
      }
      if (typeof start === 'function') start();
    }

    if (!el || typeof start !== 'function') {
      run();
      return run;
    }

    if (el.addEventListener) {
      el.addEventListener('pointerdown', run, { passive: true });
      el.addEventListener('focusin', run);
    }

    if (typeof IntersectionObserver === 'undefined') {
      run();
      return run;
    }

    if (isNearViewport(el, leadPx)) {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
      else run();
      return run;
    }

    io = new IntersectionObserver(
      function (entries) {
        if (!entries[0] || !entries[0].isIntersecting) return;
        run();
      },
      { root: null, rootMargin: leadPx + 'px 0px', threshold: 0.01 }
    );
    io.observe(el);
    return run;
  }

  w.AMCWorkshopEventsV = {
    get: get,
    prefetch: prefetch,
    bootPage: bootPage,
    collectPageGids: collectPageGids,
    whenNearViewport: whenNearViewport,
    whenNearOrIdle: whenNearOrIdle,
    US_LAT: US_LAT,
    US_LNG: US_LNG,
    US_RADIUS: US_RADIUS
  };

  schedulePageBoot();
})(window);
